import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';

interface RightPanelProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  headerExtras?: React.ReactNode;
  widthClass?: string; // e.g., responsive width classes
  children: React.ReactNode;
}

const RightPanel: React.FC<RightPanelProps> = ({ isOpen, onClose, title, headerExtras, widthClass = 'md:w-[680px] lg:w-[780px] xl:w-[900px]', children }) => {
  const [headerTop, setHeaderTop] = useState<number>(0);

  useEffect(() => {
    const measure = () => {
      const el = (document.getElementById('app-header') || document.querySelector('header')) as HTMLElement | null;
      setHeaderTop(el?.offsetHeight || 0);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const content = (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed left-0 right-0 bottom-0 z-50"
          style={{ top: headerTop }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/30" onClick={onClose} />
          <motion.div
            className={`absolute right-0 top-0 h-full w-full ${widthClass} theme-bg-primary backdrop-blur-md shadow-2xl theme-border border-l flex flex-col`}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.25 }}
          >
            <div className="flex items-center justify-between p-3 theme-border-t border-b theme-bg-secondary">
              <div className="min-w-0 mr-2">{typeof title === 'string' ? (<h3 className="text-sm font-semibold theme-text-primary truncate">{title}</h3>) : title}</div>
              <div className="flex items-center gap-1.5">
                {headerExtras}
                <button onClick={onClose} className="p-1.5 rounded-md theme-bg-primary hover:opacity-80 transition-opacity"><X className="w-4 h-4 theme-text-primary" /></button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3">{children}</div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return createPortal(content, document.body);
};

export default RightPanel;

